import classNames from 'classnames'
import { TFunction } from 'i18next'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionLanguage } from '@app/context/SessionSettingsContext'

import { useNavigate } from '@lib/react-router-dom'

import { TradingPoolAvailable } from '@pages/RewardsHub/components/TradingPoolAvailable'

import { Carousel } from '@ui/Carousel/Carousel'
import { CarouselChild } from '@ui/Carousel/CarouselItem'
import { Body1, Title1 } from '@ui/Typography/Typography'
import { Button } from '@ui/components/Button'
import { ButtonVariant } from '@ui/components/Button/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path-common'
import { getTradingViewPromotionUrl } from '@utils/rewardsHubUrls'

import { CampaignsAvailableToJoinDto } from '@model/RewardsHubDashboardDto'

import { Box } from '@shared/components/Box'
import { useLuckyDraw } from '@shared/components/LuckyDraw/useLuckyDraw'

import { MorePerks } from './components/MorePerks'

import styles from './AvailableRewards.module.scss'

export enum RewardTypeEnum {
  ClientRebate = 0,
  ClientContest = 1,
  LuckyDraw = 3,
  MarginBonus = 4,
  TradingPool = 5,
  TradingView = 6,
}

const rewardTypeStyleMap: Record<RewardTypeEnum, string> = {
  [RewardTypeEnum.MarginBonus]: styles.marginBonus,
  [RewardTypeEnum.ClientContest]: styles.clientContest,
  [RewardTypeEnum.ClientRebate]: styles.rebateCampaign,
  [RewardTypeEnum.LuckyDraw]: styles.luckyDraw,
  [RewardTypeEnum.TradingPool]: styles.tradingPool,
  [RewardTypeEnum.TradingView]: styles.tradingView,
}

interface AvailableRewardProps {
  campaigns: CampaignsAvailableToJoinDto[]
  rewardsToClaim: number
}

export const AvailableReward: React.FC<AvailableRewardProps> = ({ campaigns, rewardsToClaim }) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const [showAllRewards, setShowAllRewards] = useState(false)

  const REWARD_PER_ROW = 3

  if (campaigns.length === 0) {
    return (
      <MorePerks
        title={t('RewardsHub.Stay Tuned for New Opportunities')}
        body={t(
          'RewardsHub.While there are no specific offers for you right now, new campaigns and challenges are added regularly. Keep an eye on this space!'
        )}
      />
    )
  }

  if (isMobile) {
    return (
      <Carousel loop={false} interval={90000}>
        {campaigns.map((campaign) => (
          <CarouselChild key={campaign.type.id}>
            <CampaignItem campaign={campaign} rewardsToClaim={rewardsToClaim} />
          </CarouselChild>
        ))}
      </Carousel>
    )
  }

  return (
    <Box flex direction='column' gap='x32'>
      <Box flex wrap gap='x16'>
        {campaigns.map((campaign, idx) => {
          if (showAllRewards || idx < REWARD_PER_ROW) {
            return (
              <CampaignItem
                key={campaign.type.id}
                campaign={campaign}
                rewardsToClaim={rewardsToClaim}
              />
            )
          }
          return null
        })}
      </Box>
      {campaigns.length > REWARD_PER_ROW && (
        <Box fullWidth flex justify='center'>
          <Button.Ghost
            size='xl'
            onClick={() => setShowAllRewards((prev) => !prev)}
            iconRight={showAllRewards ? ArrowIcon.Up : ArrowIcon.Down}
          >
            {showAllRewards ? t('RewardsHub.View Less') : t('RewardsHub.View More')}
          </Button.Ghost>
        </Box>
      )}
    </Box>
  )
}

interface CardText {
  title: string
  body: string
}

const CampaignItem: React.FC<{ campaign: CampaignsAvailableToJoinDto; rewardsToClaim: number }> = ({
  campaign,
  rewardsToClaim,
}) => {
  const { t } = useTranslation()
  const { title, body } = getText(campaign, t, rewardsToClaim)

  return (
    <Box
      className={classNames(
        styles.rewardItem,
        rewardTypeStyleMap[campaign.type.id as RewardTypeEnum]
      )}
      flex
      direction='column'
      justify='space-between'
    >
      <Box flex direction='column' gap='x16'>
        {campaign.type.id === RewardTypeEnum.TradingPool ? (
          <TradingPoolAvailable campaign={campaign} />
        ) : (
          <>
            <Box flex gap='x4' align='center'>
              {campaign.type.id === RewardTypeEnum.TradingView && <TradingViewIcon size={24} />}
              <Title1 semibold>{title}</Title1>
            </Box>
            <Body1>{body}</Body1>
          </>
        )}
      </Box>
      <CardAction campaign={campaign} rewardsToClaim={rewardsToClaim} />
    </Box>
  )
}

type ActionType = { type: ButtonVariant; label: string; path?: string; onclick?: () => void }

const getPathWithSelectedCampaign = (
  path: string,
  campaign: CampaignsAvailableToJoinDto,
  isSecondary: boolean
): string => {
  if (isSecondary || path.startsWith('http')) {
    return path
  }

  const rewardType = campaign.type.id as RewardTypeEnum
  const shouldAttachCampaignId =
    (rewardType === RewardTypeEnum.ClientContest ||
      rewardType === RewardTypeEnum.ClientRebate ||
      rewardType === RewardTypeEnum.TradingPool ||
      rewardType === RewardTypeEnum.MarginBonus) &&
    !!campaign.campaign.id

  if (!shouldAttachCampaignId) {
    return path
  }

  const [basePath, query = ''] = path.split('?')
  const queryParams = new URLSearchParams(query)

  if (!queryParams.get('campaignId')) {
    queryParams.set('campaignId', campaign.campaign.id)
  }

  return `${basePath}?${queryParams.toString()}`
}

const getActionMap = (
  rewardsToClaim: number,
  openLuckyDrawModal: () => void,
  tradingViewLearnMoreUrl: string
): Record<RewardTypeEnum, ActionType[]> => {
  const hasSpins = rewardsToClaim > 0

  return {
    [RewardTypeEnum.MarginBonus]: [
      {
        type: 'secondary',
        label: 'RewardsHub.Learn More',
      },
      {
        type: 'brand',
        label: 'RewardsHub.Open Bonus Account',
        path: CommonPaths.NewLiveTradingAccount + '?bonusCampaign=true',
      },
    ],
    [RewardTypeEnum.ClientContest]: [
      {
        type: 'secondary',
        label: 'RewardsHub.Learn More',
      },
      {
        type: 'brand',
        label: 'RewardsHub.Join Now',
        path: CommonPaths.NewLiveTradingAccount + '?campaign=true',
      },
    ],
    [RewardTypeEnum.ClientRebate]: [
      {
        type: 'secondary',
        label: 'RewardsHub.Learn More',
      },
      {
        type: 'brand',
        label: 'RewardsHub.Open Rebate Account',
        path: CommonPaths.NewLiveTradingAccount + '?campaign=true',
      },
    ],
    [RewardTypeEnum.TradingPool]: [
      {
        type: 'secondary',
        label: 'RewardsHub.Learn More',
        path: tradingViewLearnMoreUrl,
      },
      {
        type: 'brand',
        label: 'RewardsHub.TradingPool.Get Started',
        path: CommonPaths.NewLiveTradingAccount,
      },
    ],
    [RewardTypeEnum.TradingView]: [
      {
        type: 'secondary',
        label: 'RewardsHub.Learn More',
        path: tradingViewLearnMoreUrl,
      },
      {
        type: 'brand',
        label: 'RewardsHub.Open Account',
        path: CommonPaths.NewLiveTradingAccount + '?tradingViewCampaign=true',
      },
    ],
    [RewardTypeEnum.LuckyDraw]: [
      {
        type: hasSpins ? 'brand' : 'secondary',
        label: hasSpins ? 'RewardsHub.Spin the Wheel' : 'RewardsHub.Learn More',
        onclick: openLuckyDrawModal,
      },
    ],
  }
}

const CardAction: React.FC<{
  campaign: CampaignsAvailableToJoinDto
  rewardsToClaim: number
}> = ({ campaign, rewardsToClaim }) => {
  const { t } = useTranslation()
  const { openLuckyDrawModal } = useLuckyDraw()

  const locale = useSessionLanguage()
  const MARKETING_URL = getTradingViewPromotionUrl(locale)
  const actions =
    getActionMap(rewardsToClaim, openLuckyDrawModal, MARKETING_URL)[
      campaign.type.id as RewardTypeEnum
    ] || []
  const navigate = useNavigate()

  return (
    <Box flex gap='x16' mt='x8'>
      {actions.map((action, index) => {
        const isSecondary = action.type === 'secondary'
        const url =
          isSecondary && campaign.termsAndConditionsUrl
            ? campaign.termsAndConditionsUrl
            : action.path
        const navigationUrl = url
          ? getPathWithSelectedCampaign(url, campaign, isSecondary)
          : undefined
        return (
          <Button
            key={index}
            variant={action.type as ButtonVariant}
            onClick={() => {
              if (navigationUrl && campaign.type.id !== RewardTypeEnum.LuckyDraw) {
                if (navigationUrl.startsWith('http')) {
                  window.open(navigationUrl, '_blank', 'noopener,noreferrer')
                } else {
                  return navigate(navigationUrl)
                }
              } else if (action?.onclick) {
                action.onclick()
              }
            }}
            size='sm'
          >
            {t(action.label)}
          </Button>
        )
      })}
    </Box>
  )
}

const getText = (
  campaign: CampaignsAvailableToJoinDto,
  t: TFunction,
  rewardsToClaim: number
): CardText => {
  switch (campaign.type.id as RewardTypeEnum) {
    case RewardTypeEnum.MarginBonus:
      return {
        title: campaign.campaign.name ?? t('RewardsHub.100 % Deposit Bonus'),
        body: campaign.description ?? t('RewardsHub.Get up to $2000 additional buying power.'),
      }
    case RewardTypeEnum.ClientContest:
      return {
        title: campaign.campaign.name ?? t('RewardsHub.Trading Challenge'),
        body: campaign.description ?? t('RewardsHub.Claim your share of $30,000 in Cash Prizes!'),
      }
    case RewardTypeEnum.ClientRebate:
      return {
        title: campaign.campaign.name ?? t('RewardsHub.Get Paid for Trading'),
        body: campaign.description ?? t('RewardsHub.Receive a reward on every lot you trade.'),
      }
    case RewardTypeEnum.TradingPool:
      return {
        title: campaign.campaign.name,
        body: campaign.description,
      }
    case RewardTypeEnum.TradingView:
      return {
        title: t('RewardsHub.Your TradingView Plan'),
        body: t(
          'RewardsHub.Trade with your Tickmill account on TradingView to unlock  free premium subscriptions.'
        ),
      }
    default:
      return {
        title: t('RewardsHub.Rewards Wheel'),
        body:
          rewardsToClaim > 0
            ? t('RewardsHub.Your wheel is set and ready to spin')
            : t('RewardsHub.Unlock a prize spin with every deposit.'),
      }
  }
}
